'use client';
import { useEffect } from 'react';

/**
 * Safety net for leaked body scroll-locks.
 *
 * With several modal machines in the tree (Ark dialogs/drawers, wallet
 * modal, dev overlay), overlapping open/close sequences can race the
 * save/restore of body's inline style and strand `overflow: hidden`
 * (+ scrollbar padding) after every modal has closed — the page then
 * looks frozen and sticky bars stop working (mobile bug 2026-07-10).
 *
 * The watchdog only ever acts when there is provably nothing open:
 * no Ark modal content/backdrop in open state, no Privy iframe dialog,
 * no Next.js dev-overlay portal. It clears exactly the lock properties
 * and nothing else.
 */
export const ScrollLockWatchdog = () => {
  useEffect(() => {
    const hasOpenModal = () =>
      document.querySelector(
        [
          '[data-scope="dialog"][data-part="content"][data-state="open"]',
          '[data-scope="drawer"][data-part="content"][data-state="open"]',
          '[data-part="backdrop"][data-state="open"]',
          '#privy-dialog',
          '[id^="privy-modal"]',
          'nextjs-portal',
        ].join(', '),
      ) !== null;

    let timer: ReturnType<typeof setTimeout> | null = null;

    const check = () => {
      const style = document.body.style;
      const locked =
        style.overflow === 'hidden' || style.position === 'fixed';
      if (!locked || hasOpenModal()) return;
      // grace period: a modal may be mid-mount between lock and open state
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const s = document.body.style;
        const stillLocked =
          s.overflow === 'hidden' || s.position === 'fixed';
        if (!stillLocked || hasOpenModal()) return;
        s.removeProperty('overflow');
        s.removeProperty('padding-right');
        s.removeProperty('padding-left');
        if (s.position === 'fixed') {
          // zag iOS-style lock: restore the scroll position it pinned
          const top = -parseFloat(s.top || '0') || 0;
          s.removeProperty('position');
          s.removeProperty('top');
          s.removeProperty('left');
          s.removeProperty('right');
          window.scrollTo({ top, behavior: 'instant' as ScrollBehavior });
        }
        document.body.removeAttribute('data-scroll-lock');
      }, 600);
    };

    const observer = new MutationObserver(check);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
      childList: true,
      subtree: false,
    });
    check();

    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, []);

  return null;
};
